import React, { CSSProperties } from "react";
import classNames from "classnames";

import { Note, TB303ControlState, TB303State, TB303Waveform } from "./core";
import { StepSequencer } from "./StepSequencer";
import { Knob } from "./Knob";

import * as tb303 from "./audio/tb303";
import * as sfx from "./audio/sfx";

import TB303Branding from "./assets/images/TB-303.svg";
import TB303BrandingMobile from "./assets/images/TB-303--mobile.svg";

import columnLabel1 from "./assets/images/303ColumnLabel--1.svg";
import columnLabel2 from "./assets/images/303ColumnLabel--2.svg";
import columnLabel3 from "./assets/images/303ColumnLabel--3.svg";
import columnLabel4 from "./assets/images/303ColumnLabel--4.svg";
import columnLabel5 from "./assets/images/303ColumnLabel--5.svg";
import columnLabel6 from "./assets/images/303ColumnLabel--6.svg";
import columnLabel7 from "./assets/images/303ColumnLabel--7.svg";
import columnLabel8 from "./assets/images/303ColumnLabel--8.svg";
import columnLabel9 from "./assets/images/303ColumnLabel--9.svg";
import columnLabel10 from "./assets/images/303ColumnLabel--10.svg";
import columnLabel11 from "./assets/images/303ColumnLabel--11.svg";
import columnLabel12 from "./assets/images/303ColumnLabel--12.svg";
import columnLabel13 from "./assets/images/303ColumnLabel--13.svg";
import columnLabel14 from "./assets/images/303ColumnLabel--14.svg";
import columnLabel15 from "./assets/images/303ColumnLabel--15.svg";
import columnLabel16 from "./assets/images/303ColumnLabel--16.svg";

import rowLabelC from "./assets/images/303RowLabel--C.svg";
import rowLabelCSharp from "./assets/images/303RowLabel--CSharp.svg";
import rowLabelD from "./assets/images/303RowLabel--D.svg";
import rowLabelDSharp from "./assets/images/303RowLabel--DSharp.svg";
import rowLabelE from "./assets/images/303RowLabel--E.svg";
import rowLabelF from "./assets/images/303RowLabel--F.svg";
import rowLabelFSharp from "./assets/images/303RowLabel--FSharp.svg";
import rowLabelG from "./assets/images/303RowLabel--G.svg";
import rowLabelGSharp from "./assets/images/303RowLabel--GSharp.svg";
import rowLabelA from "./assets/images/303RowLabel--A.svg";
import rowLabelASharp from "./assets/images/303RowLabel--ASharp.svg";
import rowLabelB from "./assets/images/303RowLabel--B.svg";

import buttonToggle from "./assets/images/808Button--toggle.svg";
import buttonLabelRandom from "./assets/images/303ButtonLabel--random.svg";
import buttonLabelRunStop from "./assets/images/303ButtonLabel--runStop.svg";
import buttonLabelClear from "./assets/images/303ButtonLabel--clear.svg";
import buttonLabelWaveform from "./assets/images/303ButtonLabel--waveform.svg";
import buttonLabelWaveformShapes from "./assets/images/303ButtonLabel--waveformShapes.svg";

import knobForeground from "./assets/images/303Knob.svg";
import knobBackground from "./assets/images/303KnobBackground.svg";
import knobLabelCutOffFreq from "./assets/images/303KnobLabel--cutOffFreq.svg";
import knobLabelResonance from "./assets/images/303KnobLabel--resonance.svg";
import knobLabelEnvMod from "./assets/images/303KnobLabel--envMod.svg";
import knobLabelDecay from "./assets/images/303KnobLabel--decay.svg";
import knobLabelAccent from "./assets/images/303KnobLabel--accent.svg";

import "./TB303.scss";

const ROW_HEADER_IMAGES: { [note: string]: string } = {
  C: rowLabelC,
  "C#": rowLabelCSharp,
  D: rowLabelD,
  "D#": rowLabelDSharp,
  E: rowLabelE,
  F: rowLabelF,
  "F#": rowLabelFSharp,
  G: rowLabelG,
  "G#": rowLabelGSharp,
  A: rowLabelA,
  "A#": rowLabelASharp,
  B: rowLabelB,
};

interface TB303Props {
  state: TB303State;
  onStart: () => void;
  onStop: () => void;
  onToggleHit: (timeStep: number, note: Note) => void;
  onClearHits: () => void;
  onGenerateRandomHits: () => void;
  onAdjustControl: (control: keyof TB303ControlState, value: number) => void;
  onSetWaveform: (waveform: TB303Waveform) => void;
  onSwitchDevice: () => void;
}
export const TB303: React.FC<TB303Props> = ({
  state,
  onStart,
  onStop,
  onToggleHit,
  onClearHits,
  onGenerateRandomHits,
  onAdjustControl,
  onSetWaveform,
  onSwitchDevice,
}) => {
  return (
    <div
      className={classNames("tb303", `waveform-${state.waveform}`)}
      style={{ "--numTimeSteps": state.layout.timeSteps } as CSSProperties}
    >
      <svg
        className="logo"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 157.08 24.15"
      >
        <path d="M90.93.94V22.52a.93.93,0,0,0,.94.94h2.57a.93.93,0,0,0,.94-.94V.94A.92.92,0,0,0,94.44,0H91.87A.92.92,0,0,0,90.93.94Z" />
        <path d="M135.4,22.52V13a6.93,6.93,0,0,0-6.48-6.87,10.55,10.55,0,0,0-6.81,2.15c-.16.13-.6.26-.6-.13v-1a.91.91,0,0,0-.93-.93h-2.22a.92.92,0,0,0-.94.93V22.52a.92.92,0,0,0,.94.94h2.22a.92.92,0,0,0,.93-.94V15a5.6,5.6,0,0,1,6-5.61c3.28,0,3.8,2.26,3.8,3.6v9.51a.91.91,0,0,0,.94.93h2.21A.92.92,0,0,0,135.4,22.52Z" />
        <path d="M97.83,11v.1" />
        <path d="M147.1,9.5a5.52,5.52,0,0,1,5.79,5.61,5.72,5.72,0,1,1-11.43,0A5.55,5.55,0,0,1,147.1,9.5ZM152.45,22c.27-.21.55,0,.54.46a.94.94,0,0,0,.94,1h2.22a.91.91,0,0,0,.93-.94V.94a.9.9,0,0,0-.93-.93h-2.22a.91.91,0,0,0-.94.93V7.75c0,.5-.27.67-.54.46-6-4.63-15.22,0-15.2,6.85C137.27,22.72,146.32,26.85,152.45,22Z" />
        <path d="M102.32,16.77c2.12-1.12,7.34-1,7.82-.95.25,0,.6.21.59.55,0,4.52-6.18,5.13-8.54,3.57a1.86,1.86,0,0,1-.83-1.43A1.75,1.75,0,0,1,102.32,16.77Zm-2.75,6.07c2.9,2.07,8.29,1.57,10.74-.58a.45.45,0,0,1,.74.26c.24.75.59.93,1.34.94h1.53a.91.91,0,0,0,.93-.94V12.81c0-5.28-4.92-6.68-8.7-6.68-4.22,0-7,1.61-8.32,4.87-.14.33.26.73.59.73h2.88a1.59,1.59,0,0,0,1.14-.66c.74-1.26,2-1.72,4.23-1.72,2.51,0,3.17.56,4,1.72a1,1,0,0,1-.58,1.54,61,61,0,0,0-8.73.8,5.15,5.15,0,0,0-1.78,9.42ZM78.86,24.06c7.48,0,10.61-4.85,10.61-9,0-3.93-3.15-9-10.67-9s-10.65,5-10.65,9C68.15,19.29,71.32,24.06,78.86,24.06Zm0-3.21c-4.37,0-6.37-3.24-6.37-5.72a6.35,6.35,0,0,1,12.64,0A6,6,0,0,1,78.86,20.85Zm-28.67-7v8.67a.91.91,0,0,1-.94.93H47.16a.91.91,0,0,1-.95-.93V.93A.92.92,0,0,1,47.15,0h13.6a6.37,6.37,0,0,1,6.59,6.45,6.55,6.55,0,0,1-2.9,5.24c-.14.09-.24.27-.18.32,1.17.92,1.18,1.66,1.59,3.23.73,2.84.82,5.32,1.94,7.7a.42.42,0,0,1-.37.5H64.71a1.45,1.45,0,0,1-1.54-.94c-1-3-.31-5.85-2.33-8.67a3.35,3.35,0,0,0-2.76-.94H51.13A.92.92,0,0,0,50.19,13.84Zm9.24-4.05s3.65,0,3.65-3.34S59.43,3.1,59.43,3.1h-8.3a.92.92,0,0,0-.94.94V8.85a1,1,0,0,0,.94.94Z" />
        <path d="M37.09,10.29h-23A5.67,5.67,0,0,0,8.32,16a5.81,5.81,0,0,0,5.8,5.75H42.21v1.7H14.09a7.44,7.44,0,1,1,0-14.87H35.32V6.25H6.53V23.44H4.64V4.53H37.09Z" />
        <path d="M12.36,18.93H42.21V17.22H14.12V14.89H35.37a7.35,7.35,0,0,0,7.55-7.43A7.46,7.46,0,0,0,35.26,0H0V23.44H1.89V1.72H35.32a5.83,5.83,0,0,1,5.82,5.76,5.68,5.68,0,0,1-5.82,5.69h-23Z" />
      </svg>
      <StepSequencer<Note>
        timeSteps={state.layout.timeSteps}
        rows={state.layout.rows}
        rowHeaderImages={ROW_HEADER_IMAGES}
        currentHits={state.sequence}
        marchProvider={tb303.subscribeToMarch}
        onToggleHit={onToggleHit}
      />
      <img
        className="branding"
        src={TB303Branding}
        alt="TB-303, Computer Controlled. Bass Line"
      />
      <img
        className="branding--mobile"
        src={TB303BrandingMobile}
        alt="TB-303. Bass Line"
      />
      <div className="controls">
        <div className="columnLabels">
          <div className="columnProp">
            <svg
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16.24 10"
            >
              <rect x="0" y="0" width="16.24" height="10" />
            </svg>
          </div>
          <div className="columnLabel">
            <img src={columnLabel1} alt="1" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel2} alt="2" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel3} alt="3" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel4} alt="4" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel5} alt="5" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel6} alt="6" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel7} alt="7" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel8} alt="8" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel9} alt="9" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel10} alt="10" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel11} alt="11" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel12} alt="12" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel13} alt="13" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel14} alt="14" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel15} alt="15" />
          </div>
          <div className="columnLabel">
            <img src={columnLabel16} alt="16" />
          </div>
        </div>
        <div className="switches">
          <div className="switch switch--random">
            <img
              className="buttonLabel buttonLabel--random"
              src={buttonLabelRandom}
              alt="Random"
            />
            <button
              className="button button--random"
              onClick={() => {
                onGenerateRandomHits();
                sfx.triggerTB303RunClearRandom();
              }}
            >
              <span className="sr-only">Random</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64.64 36.32">
                <rect
                  className="background"
                  x="2"
                  y="2"
                  width="60.64"
                  height="32.32"
                />
                <path
                  className="border"
                  d="M60.64,4V32.32H4V4H60.64m4-4H0V36.32H64.64V0Z"
                />
              </svg>
            </button>
          </div>
          <div className="knobs">
            <Knob
              value={state.controls.cutOffFreq}
              min={tb303.CUTOFF_MIN}
              max={tb303.CUTOFF_MAX}
              step={tb303.CUTOFF_STEP}
              classNameSuffix="cutOffFeq"
              labelText="Cut Off Frequency"
              labelImgUrl={knobLabelCutOffFreq}
              knobImgUrl={knobForeground}
              knobBackgroundImgUrl={knobBackground}
              onChange={(val) => onAdjustControl("cutOffFreq", val)}
            />
            <Knob
              value={state.controls.resonance}
              min={tb303.Q_MIN}
              max={tb303.Q_MAX}
              step={tb303.Q_STEP}
              classNameSuffix="resonance"
              labelText="Resonance"
              labelImgUrl={knobLabelResonance}
              knobImgUrl={knobForeground}
              knobBackgroundImgUrl={knobBackground}
              onChange={(val) => onAdjustControl("resonance", val)}
            />
            <Knob
              value={state.controls.envMod}
              min={tb303.F_MOD_MIN}
              max={tb303.F_MOD_MAX}
              step={tb303.F_MOD_STEP}
              classNameSuffix="envMod"
              labelText="Env Mod"
              labelImgUrl={knobLabelEnvMod}
              knobImgUrl={knobForeground}
              knobBackgroundImgUrl={knobBackground}
              onChange={(val) => onAdjustControl("envMod", val)}
            />
            <Knob
              value={state.controls.decay}
              min={tb303.DECAY_MIN}
              max={tb303.DECAY_MAX}
              step={tb303.DECAY_STEP}
              classNameSuffix="decay"
              labelText="Decay"
              labelImgUrl={knobLabelDecay}
              knobImgUrl={knobForeground}
              knobBackgroundImgUrl={knobBackground}
              onChange={(val) => onAdjustControl("decay", val)}
            />
            <Knob
              value={state.controls.accent}
              min={tb303.ACCENT_MIN}
              max={tb303.ACCENT_MAX}
              step={tb303.ACCENT_STEP}
              classNameSuffix="accent"
              labelText="Accent"
              labelImgUrl={knobLabelAccent}
              knobImgUrl={knobForeground}
              knobBackgroundImgUrl={knobBackground}
              onChange={(val) => onAdjustControl("accent", val)}
            />
          </div>
          <div className="switch switch--waveform">
            <img
              className="buttonLabel buttonLabel--waveform"
              src={buttonLabelWaveform}
              alt="Waveform"
            />
            <div className="waveformToggle">
              <label className="waveformToggle--sawtooth">
                <span className="sr-only">Sawtooth</span>
                <input
                  className="offscreen"
                  type="radio"
                  name="waveform"
                  value="sawtooth"
                  checked={state.waveform === "sawtooth"}
                  onChange={() => {
                    onSetWaveform("sawtooth");
                    sfx.triggerTB303WaveformSwitch();
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32.32 25.06"
                >
                  <polygon points="30.32 0 2 0 0 0 0 2.76 0 22.3 0 25.06 2 25.06 30.32 25.06 32.32 25.06 32.32 22.3 32.32 2.76 32.32 0 30.32 0" />
                </svg>
              </label>
              <label className="waveformToggle--square">
                <span className="sr-only">Square</span>
                <input
                  className="offscreen"
                  type="radio"
                  name="waveform"
                  value="square"
                  checked={state.waveform === "square"}
                  onChange={() => {
                    onSetWaveform("square");
                    sfx.triggerTB303WaveformSwitch();
                  }}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 32.32 25.06"
                >
                  <polygon points="30.32 0 2 0 0 0 0 2.76 0 22.3 0 25.06 2 25.06 30.32 25.06 32.32 25.06 32.32 22.3 32.32 2.76 32.32 0 30.32 0" />
                </svg>
              </label>
            </div>
            <img
              className="buttonLabel buttonLabel--waveformShapes"
              src={buttonLabelWaveformShapes}
              alt="Sawtooth and square waveform illustrations"
            />
          </div>
          <div className="switch switch--runStop">
            <img
              className="buttonLabel buttonLabel--runStop"
              src={buttonLabelRunStop}
              alt="Run/Stop"
            />
            <div className="runStopToggle">
              <button
                className="button button--runStop button--runStopRun"
                onClick={() => {
                  onStart();
                  sfx.triggerTB303RunClearRandom();
                }}
              >
                <span className="sr-only">Run</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 89.93 60.98"
                >
                  <rect
                    className="inner"
                    x="2"
                    y="2"
                    width="85.93"
                    height="56.98"
                  />
                  <path
                    className="border"
                    d="M85.93,4V57H4V4H85.93m4-4H0V61H89.93V0Z"
                  />
                  <rect
                    className="inner"
                    x="4"
                    y="4"
                    width="81.93"
                    height="52.98"
                  />
                  <rect
                    className="inner"
                    x="19.29"
                    y="4"
                    width="51.35"
                    height="52.98"
                  />
                  <rect
                    className="outer"
                    x="4"
                    y="4"
                    width="15.29"
                    height="52.98"
                  />
                  <rect
                    className="outer"
                    x="70.64"
                    y="4"
                    width="15.29"
                    height="52.98"
                  />
                </svg>
              </button>
              <button
                className={classNames(
                  "button button--runStop button--runStopStop",
                  {
                    isVisible: state.playing,
                  }
                )}
                onClick={() => {
                  onStop();
                  sfx.triggerTB303RunClearRandom();
                }}
              >
                <span className="sr-only">Stop</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 89.93 60.98"
                >
                  <rect
                    className="inner"
                    x="2"
                    y="2"
                    width="85.93"
                    height="56.98"
                  />
                  <path
                    className="border"
                    d="M85.93,4V57H4V4H85.93m4-4H0V61H89.93V0Z"
                  />
                  <rect
                    className="inner"
                    x="4"
                    y="4"
                    width="81.93"
                    height="52.98"
                  />
                  <rect
                    className="inner"
                    x="19.29"
                    y="4"
                    width="51.35"
                    height="52.98"
                  />
                  <rect
                    className="outer"
                    x="4"
                    y="4"
                    width="15.29"
                    height="52.98"
                  />
                  <rect
                    className="outer"
                    x="70.64"
                    y="4"
                    width="15.29"
                    height="52.98"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="switch switch--clear">
            <img
              className="buttonLabel buttonLabel--clear"
              src={buttonLabelClear}
              alt="Clear"
            />
            <button
              className="button button--clear"
              onClick={() => {
                onClearHits();
                sfx.triggerTB303RunClearRandom();
              }}
            >
              <span className="sr-only">Clear</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64.64 36.32">
                <rect
                  className="background"
                  x="2"
                  y="2"
                  width="60.64"
                  height="32.32"
                />
                <path
                  className="border"
                  d="M60.64,4V32.32H4V4H60.64m4-4H0V36.32H64.64V0Z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <button
        className="button button--deviceSwitcher"
        onClick={onSwitchDevice}
      >
        <img src={buttonToggle} alt="TR-808" />
      </button>
    </div>
  );
};
